import React, { useState } from "react";
import { base44 } from "../api/base44Client";
import { useMutation } from "@tanstack/react-query";
import { Hammer, Pencil, Building, MessageSquare, CheckCircle, Phone, Mail, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";

export default function Services() {
  const [formData, setFormData] = useState({ client_name:'', client_email:'', client_phone:'', service_type:'', description:'', budget:'' });
  const [showForm, setShowForm] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [success, setSuccess] = useState(false);

  const services = [
    { id:'plan_2d', icon:Pencil, title:'Plans 2D', description:'Conception de plans architecturaux détaillés en 2D pour votre projet de construction', features:['Plans de façade','Plans de niveau','Plans de toiture','Cotations précises','Fichiers modifiables'], color:'from-blue-500 to-blue-600' },
    { id:'plan_3d', icon:Building, title:'Modélisation 3D', description:'Visualisation réaliste de votre futur projet avec rendu 3D professionnel', features:['Modélisation 3D complète','Rendus photoréalistes','Visite virtuelle','Animations 3D','Modifications illimitées'], color:'from-purple-500 to-purple-600' },
    { id:'construction', icon:Hammer, title:'Construction', description:'Gestion complète de votre projet de construction de A à Z', features:['Étude de faisabilité','Gestion de chantier','Suivi des travaux','Contrôle qualité','Livraison clé en main'], color:'from-orange-500 to-orange-600' },
    { id:'consultation', icon:MessageSquare, title:'Consultation', description:"Conseils d'experts pour optimiser votre projet immobilier", features:['Analyse de terrain','Conseils réglementaires','Optimisation budget','Recommandations personnalisées','Suivi post-consultation'], color:'from-green-500 to-green-600' }
  ];

  const createRequestMutation = useMutation({
    mutationFn: async (requestData) => base44.entities.ServiceRequest.create(requestData),
    onSuccess: () => {
      setSuccess(true);
      setFormData({ client_name:'', client_email:'', client_phone:'', service_type:'', description:'', budget:'' });
      setShowForm(false);
      setSelectedService(null);
      setTimeout(() => setSuccess(false), 5000);
    }
  });

  const handleSubmit = (e) => { e.preventDefault(); createRequestMutation.mutate(formData); };
  const openForm = (serviceId) => {
    const service = services.find(s => s.id === serviceId);
    setSelectedService(service);
    setFormData({...formData, service_type:serviceId});
    setShowForm(true);
    setTimeout(() => document.getElementById('service-form')?.scrollIntoView({ behavior:'smooth', block:'center' }), 100);
  };
  const closeForm = () => { setShowForm(false); setSelectedService(null); setFormData({ client_name:'', client_email:'', client_phone:'', service_type:'', description:'', budget:'' }); };

  return (
    <div className="min-h-screen bg-gray-50">
      <section className="gradient-bg text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity:0,y:20 }} animate={{ opacity:1,y:0 }} className="text-center">
            <Hammer className="w-16 h-16 mx-auto mb-4 text-yellow-400" />
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Nos Services de Construction</h1>
            <p className="text-xl text-gray-200">De la conception à la réalisation de vos projets</p>
          </motion.div>
        </div>
      </section>

      {success && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
          <div className="bg-green-50 border border-green-200 flex items-center gap-2 p-4 rounded-lg">
            <CheckCircle className="w-4 h-4 text-green-600" />
            <span className="text-green-800">Votre demande a été envoyée avec succès ! Nous vous contactons bientôt.</span>
          </div>
        </div>
      )}

      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            {services.map((service, index) => (
              <motion.div key={service.id} initial={{ opacity:0,y:20 }} animate={{ opacity:1,y:0 }} transition={{ delay:index*0.1 }}>
                <div className="border-none shadow-xl hover:shadow-2xl transition-all h-full flex flex-col rounded-xl bg-white overflow-hidden">
                  <div className={`h-32 bg-gradient-to-br ${service.color} flex items-center justify-center`}>
                    <service.icon className="w-16 h-16 text-white" />
                  </div>
                  <div className="p-6 flex-1 flex flex-col">
                    <h3 className="text-2xl font-bold text-black mb-3">{service.title}</h3>
                    <p className="text-gray-600 mb-4">{service.description}</p>
                    <div className="space-y-2 mb-6 flex-1">
                      {service.features.map((feature, idx) => (
                        <div key={idx} className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" /><span className="text-sm text-gray-700">{feature}</span></div>
                      ))}
                    </div>
                    <button className="w-full accent-gradient text-white hover:opacity-90 font-bold py-4 text-lg rounded-lg flex items-center justify-center gap-2" onClick={() => openForm(service.id)}>
                      Demander un Devis<ArrowRight className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {showForm && (
            <motion.div id="service-form" initial={{ opacity:0,y:20 }} animate={{ opacity:1,y:0 }}>
              <div className="border-none shadow-2xl max-w-3xl mx-auto rounded-xl overflow-hidden">
                <div className="gradient-bg text-white p-6">
                  <h3 className="text-2xl font-bold flex items-center gap-3">
                    {selectedService && <selectedService.icon className="w-8 h-8" />}
                    Demande de Devis - {selectedService?.title}
                  </h3>
                </div>
                <div className="p-8 bg-white">
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {[{label:"Nom complet *",type:"text",field:"client_name",placeholder:"Votre nom"},{label:"Email *",type:"email",field:"client_email",placeholder:"votre@email.com"},{label:"Téléphone *",type:"tel",field:"client_phone",placeholder:"+225 0585868767"}].map(f => (
                        <div key={f.field}>
                          <label className="block text-sm font-medium text-gray-700 mb-1">{f.label}</label>
                          <input type={f.type} required value={formData[f.field]} onChange={e => setFormData({...formData,[f.field]:e.target.value})} placeholder={f.placeholder}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black" />
                        </div>
                      ))}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Service demandé *</label>
                        <select required value={formData.service_type} onChange={e => setFormData({...formData,service_type:e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black">
                          <option value="">Sélectionner</option>
                          <option value="plan_2d">Plans 2D</option>
                          <option value="plan_3d">Modélisation 3D</option>
                          <option value="construction">Construction</option>
                          <option value="consultation">Consultation</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Description du projet *</label>
                      <textarea required rows={5} value={formData.description} onChange={e => setFormData({...formData,description:e.target.value})} placeholder="Décrivez votre projet en détail..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Budget estimé (optionnel)</label>
                      <input type="text" value={formData.budget} onChange={e => setFormData({...formData,budget:e.target.value})} placeholder="Ex: 5 000 000 - 10 000 000 XOF"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black" />
                    </div>
                    <div className="flex gap-4">
                      <button type="button" onClick={closeForm} className="flex-1 border border-gray-300 text-gray-700 py-3 rounded-lg hover:bg-gray-50">Annuler</button>
                      <button type="submit" disabled={createRequestMutation.isPending} className="flex-1 accent-gradient text-white py-3 rounded-lg hover:opacity-90 font-bold">
                        {createRequestMutation.isPending ? 'Envoi en cours...' : 'Envoyer la demande'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-black mb-12">Notre Processus</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[{step:"1",title:"Demande",desc:"Remplissez le formulaire de demande"},{step:"2",title:"Étude",desc:"Nous étudions votre projet"},{step:"3",title:"Devis",desc:"Vous recevez un devis détaillé"},{step:"4",title:"Réalisation",desc:"Nous réalisons votre projet"}].map((item,index) => (
              <motion.div key={index} initial={{ opacity:0,y:20 }} whileInView={{ opacity:1,y:0 }} transition={{ delay:index*0.1 }} viewport={{ once:true }} className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 accent-gradient rounded-full flex items-center justify-center text-white text-2xl font-bold">{item.step}</div>
                <h3 className="text-xl font-bold text-black mb-2">{item.title}</h3>
                <p className="text-gray-600">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 gradient-bg text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Besoin d'un Service Personnalisé ?</h2>
          <p className="text-xl text-gray-200 mb-8">Notre équipe est à votre écoute pour répondre à tous vos besoins</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="accent-gradient text-white px-8 py-4 text-lg rounded-lg hover:opacity-90 flex items-center gap-2 justify-center"><Phone className="w-5 h-5" />+225 0585868767</button>
            <button className="bg-white text-black px-8 py-4 text-lg rounded-lg hover:bg-gray-100 flex items-center gap-2 justify-center"><Mail className="w-5 h-5" />contact@r2bentreprises.com</button>
          </div>
        </div>
      </section>
    </div>
  );
}
